import {
  between, maxLength, minLength, integer, requiredIf,
} from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import { StringMap, TypeCampaignEnum } from '@/types';
import {
  checkIfTemplateOrRedirectContainsVoucher,
  getTemplateAndRedirect,
  reusePreviousCouponValues,
  voucherMethodsValues,
  voucherTypeValues,
  migrateOldVoucherConfig,
  buildTemplateVouchersRules,
  TemplateVouchersData,
  getDefaultEnabledOperator,
} from '@/composables/flows';
import { asInt } from '@/helpers';
import { getDefaultUtmFields } from '@/components/flows/operators/shared/utmDefaults';
import { localizedTextInputValidator, validateSenderDomain, requiredWithMessage } from '@/helpers/CustomValidator';
import { GetShopsConfigurationList, SaveShopsConfiguration } from '@/composables/shop/ShopsConfiguration';
import type { FlowOperatorMetadata, ValidationResult } from '@/types/flow-types';
import Form from './Form.vue';
import { canDeleteSendingAction } from '@/components/flows/operators/registry';


export interface SendEmailData {
  template_display_selector_id: number | null;
  reuse_a_previous_coupon: number;
  display_popup_on_site: number;
  template_display_reduction_method: number;
  template_display_reduction_descript: StringMap;
  template_display_reduction_type: number;
  template_display_reduction_identifiant: string;
  template_display_reduction_amount: number;
  template_display_reduction_duplicate: string;
  template_display_reduction_minorderamount: number;
  template_display_reduction_validtime: number;
  template_display_reduction_prefix: string;
  template_vouchers?: TemplateVouchersData;
  template_display_message_langs: string;
  template_message_subject: StringMap;
  template_message_preview_text: StringMap;
  template_message_expname: StringMap;
  template_message_expmail: StringMap;
  template_message_replytomail: StringMap;
  enabled_operator: number;
  [key: string]: any;
}

export function createDefaultData(data: Partial<SendEmailData> = {}, languages: string[] = [], campaignType?: string): SendEmailData {
  return {
    template_display_selector_id: data.template_display_selector_id != null ? asInt(data.template_display_selector_id) : null,
    reuse_a_previous_coupon: asInt(data.reuse_a_previous_coupon ?? 0),
    display_popup_on_site: asInt(data.display_popup_on_site ?? 0),
    template_display_reduction_method: asInt(data.template_display_reduction_method ?? 3),
    template_display_reduction_descript: data.template_display_reduction_descript ?? {},
    template_display_reduction_type: asInt(data.template_display_reduction_type ?? 1),
    template_display_reduction_identifiant: data.template_display_reduction_identifiant ?? '',
    template_display_reduction_amount: asInt(data.template_display_reduction_amount ?? 0),
    template_display_reduction_duplicate: data.template_display_reduction_duplicate ?? '',
    template_display_reduction_minorderamount: asInt(data.template_display_reduction_minorderamount ?? 0),
    template_display_reduction_validtime: asInt(data.template_display_reduction_validtime ?? 7),
    template_display_reduction_prefix: data.template_display_reduction_prefix ?? '',
    template_vouchers: data.template_vouchers && typeof data.template_vouchers === 'object' ? data.template_vouchers : {},
    template_display_message_langs: data.template_display_message_langs ?? JSON.stringify(languages),
    template_message_subject: data.template_message_subject ?? {},
    template_message_preview_text: data.template_message_preview_text ?? {},
    template_message_expname: data.template_message_expname ?? {},
    template_message_expmail: data.template_message_expmail ?? {},
    template_message_replytomail: data.template_message_replytomail ?? {},
    enabled_operator: asInt(data.enabled_operator ?? getDefaultEnabledOperator('boxsendemail', campaignType)),
    ...getDefaultUtmFields(data),
  };
}

interface ValidateContext {
  shopId: number;
}

const SendEmail: FlowOperatorMetadata<SendEmailData> = {
  id: 'boxsendemail',
  kind: 'action',
  label: 'automatedScenarios.actions.sendEmail.label',
  icon: 'fa-envelope',
  hasConfiguration: true,
  availableIn: [TypeCampaignEnum.AUTOMATION, TypeCampaignEnum.BULK],
  canToggleEnabled: true,
  requiredServices: ['EmailServiceParameter', 'SocialNetworkServiceParameter'],

  menuActions: [
    {
      id: 'send-test-email',
      label: 'campaigns.common.operatorActions.sendTestMessage',
      icon: 'far fa-paper-plane',
      action: 'send-test-email',
    },
  ],

  incompatibilities: {
    [TypeCampaignEnum.AUTOMATION]: {
      nextOperators: [
        'boxabandonpanier', 'boxsitepasstime', 'boxquitsite',
        'boxperiodiclaunching', 'boxscrollpage', 'boxstatuscommand',
        'boxcustomjs', 'registeronsite', 'subscribetonewsletter',
        'boxsendemail', 'boxdisplayfreepopup', 'boxdisplayfreemodel',
        'boxvisitpage',
      ],
    },
    [TypeCampaignEnum.BULK]: {
      nextOperators: [
        'boxabandonpanier', 'boxsitepasstime', 'boxquitsite',
        'boxperiodiclaunching', 'boxscrollpage', 'boxstatuscommand',
        'boxcustomjs', 'registeronsite', 'subscribetonewsletter',
        'boxsendemail', 'boxdisplayfreepopup', 'boxdisplayfreemodel',
        'boxvisitpage', 'boxchoosebulklist', 'boxaudiencesetup',
      ],
    },
  },

  Form,

  defaultData: (data?: Partial<SendEmailData>, campaignType?: string) => createDefaultData(data, [], campaignType),

  outputs: () => 1,

  async validate(data: SendEmailData, context?: ValidateContext): Promise<ValidationResult> {
    let mustConfigVoucher = false;
    let mustConfigMultiVoucher = false;
    // Store template IDs that have vouchers (hasVoucher = true in config)
    const templatesWithVoucher: string[] = [];

    if (data.template_display_selector_id && data.template_display_selector_id > 0 && context?.shopId) {
      const idTemplate = asInt(data.template_display_selector_id);
      let templatesContainsVoucher = false;
      const listTemplate = await getTemplateAndRedirect(context.shopId, idTemplate);
      if (listTemplate !== null) {
        templatesContainsVoucher = checkIfTemplateOrRedirectContainsVoucher(listTemplate);

        // Check if any template has hasVoucher = true in configuration
        listTemplate.forEach((template) => {
          if (typeof template.template_config === 'string') {
            const templateConfig = JSON.parse(template.template_config);
            if (templateConfig.hasVoucher) {
              templatesWithVoucher.push(String(template.id_template));
            }
            // Clean up stale disabled flag if template is no longer conditional
            const tId = String(template.id_template);
            if (data.template_vouchers?.[tId]?.disabled && !templateConfig.hasConditionalVoucher) {
              data.template_vouchers[tId].disabled = false;
            }
          }
        });
      }
      // For multi-voucher system: require config if any template has hasVoucher = true
      if (templatesWithVoucher.length > 0) {
        mustConfigMultiVoucher = true;
      } else if (templatesContainsVoucher && data.reuse_a_previous_coupon === reusePreviousCouponValues.CONFIGURE_NEW_VOUCHER) {
        // For old system: require config only if reuse_a_previous_coupon is CONFIGURE_NEW_VOUCHER
        mustConfigVoucher = true;
      }
    }

    // Migrate old config to template_vouchers and clean up obsolete configs
    // Always call this to ensure obsolete configs are removed when templates no longer have vouchers
    migrateOldVoucherConfig(data, templatesWithVoucher);

    const rules: any = {
      template_display_reduction_method: {
        required: mustConfigVoucher,
      },
      template_display_reduction_descript: {
        required: false,
      },
      template_display_reduction_type: {
        required: mustConfigVoucher,
      },
      template_display_reduction_amount: {
        required: requiredIf(() => mustConfigVoucher && [voucherTypeValues.PERCENTAGE, voucherTypeValues.AMOUNT].includes(data.template_display_reduction_type)),
        integer,
      },
      template_display_reduction_minorderamount: {
        required: requiredIf(() => mustConfigVoucher && data.template_display_reduction_method === voucherMethodsValues.AUTOMATIC),
        between: between(0, 10000000),
      },
      template_display_reduction_prefix: {
        minLength: minLength(2),
        maxLength: maxLength(32),
      },
      template_message_subject: {
        localizedTextInputValidator: localizedTextInputValidator('text'),
      },
      template_message_expname: {
        localizedTextInputValidator: localizedTextInputValidator('text'),
      },
      template_message_expmail: {
        localizedTextInputValidator: localizedTextInputValidator('email'),
        validateSenderDomain: await validateSenderDomain(),
      },
    };

    if (mustConfigVoucher && data.template_display_reduction_method === voucherMethodsValues.MANUAL) {
      rules.template_display_reduction_identifiant = {
        required: true,
        minLength: minLength(2),
        maxLength: maxLength(45),
      };
    }

    if (mustConfigVoucher && data.template_display_reduction_method === voucherMethodsValues.AUTOMATIC && data.template_display_reduction_type === voucherTypeValues.DUPLICATED) {
      rules.template_display_reduction_duplicate = {
        required: true,
        minLength: minLength(2),
        maxLength: maxLength(45),
      };
    }

    if (mustConfigVoucher && data.template_display_reduction_method === voucherMethodsValues.AUTOMATIC) {
      rules.template_display_reduction_validtime = {
        required: true,
        between: between(1, 182),
      };
    }

    // Add dynamic validation rules for template_vouchers (only if multi-voucher system is used)
    if (mustConfigMultiVoucher) {
      Object.assign(rules, buildTemplateVouchersRules(data.template_vouchers, templatesWithVoucher));
    }

    const v$ = useVuelidate(rules, data);
    const success = await v$.value.$validate();

    return {
      success,
      validate: v$,
    };
  },

  async callback(data: SendEmailData, context?: { shopId: number }): Promise<void> {
    if (!context?.shopId) return;

    const { shopId } = context;

    // Load sender names from database
    const senderNames = await GetShopsConfigurationList({
      shopId,
      key: 'select2_list_emails_from_name',
      fields: 'id_shop_configuration, key, value',
      limit: 0,
      offset: 0,
    });

    let suggestions: string[] = [];
    if (senderNames && senderNames.length && senderNames[0].value !== '') {
      const json = JSON.parse(senderNames[0].value);
      if (json) {
        json.forEach((element: string) => {
          suggestions.push(element);
        });
      }
    }

    // Add new entries if not in array
    let saveConfig = false;
    Object.keys(data.template_message_expname).forEach((key: string) => {
      if (!suggestions.includes(data.template_message_expname[key])) {
        suggestions.push(data.template_message_expname[key]);
        saveConfig = true;
      }
    });

    // Save
    if (saveConfig) {
      await SaveShopsConfiguration({
        id_shop: shopId,
        configs: [
          {
            key: 'select2_list_emails_from_name',
            lang: '',
            value: JSON.stringify(suggestions),
          },
        ],
      });
    }

    // Load sender addresses from database
    const senderAddresses = await GetShopsConfigurationList({
      shopId,
      key: 'select2_list_emails_from_address',
      fields: 'id_shop_configuration, key, value',
      limit: 0,
      offset: 0,
    });

    suggestions = [];
    if (senderAddresses && senderAddresses.length && senderAddresses[0].value !== '') {
      const json = JSON.parse(senderAddresses[0].value);
      if (json) {
        json.forEach((element: string) => {
          suggestions.push(element);
        });
      }
    }

    // Add new entries if not in array
    saveConfig = false;
    Object.keys(data.template_message_expmail).forEach((key: string) => {
      if (!suggestions.includes(data.template_message_expmail[key])) {
        suggestions.push(data.template_message_expmail[key]);
        saveConfig = true;
      }
    });
    Object.keys(data.template_message_replytomail).forEach((key: string) => {
      if (!suggestions.includes(data.template_message_replytomail[key])) {
        suggestions.push(data.template_message_replytomail[key]);
        saveConfig = true;
      }
    });

    // Save
    if (saveConfig) {
      await SaveShopsConfiguration({
        id_shop: shopId,
        configs: [
          {
            key: 'select2_list_emails_from_address',
            lang: '',
            value: JSON.stringify(suggestions),
          },
        ],
      });
    }
  },

  canBeDeleted(operatorId: string, flow: any): Promise<boolean | string> {
    return canDeleteSendingAction(flow, operatorId);
  },
};

export default SendEmail;
